import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Spacing } from '@/shared/theme';
import { Modal as MuiModal, Fade } from '@mui/material';
import CloseCross from '@/assets/images/close-cross.svg';
import { Column } from './ui';

export interface ModalProps {
  children?: React.ReactNode;
  footer?: React.ReactNode;
  open: boolean;
  onClose: () => void;
  maxWidth?: number;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
}

const StyledBox = styled(Column)<{ $maxWidth: ModalProps['maxWidth'] }>`
  background-color: #fff;
  box-shadow: 0px 8px 20px rgba(101, 125, 146, 0.12);
  border-radius: 16px;
  padding: ${Spacing.x3}px;
  outline: none;
  width: calc(100% - ${Spacing.x4}px);
  max-width: ${({ $maxWidth }) => $maxWidth ?? 400}px;
  box-sizing: border-box;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 768px) {
    max-height: 75vh;
  }
`;

const CloseButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  position: relative;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
`;

const ModalContent = styled.div`
  padding: 0 ${Spacing.x1}px;
  padding-bottom: ${Spacing.x1}px;
`;

const GradientFade = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  pointer-events: none;
  margin-top: -100px;
`;

const FixedFooter = styled.div`
  flex-shrink: 0;
  padding-top: ${Spacing.x2}px;
`;

const CloseRow = ({ onClick = () => {} }) => (
  <CloseButtonContainer>
    <CloseButton type="button" onClick={onClick} aria-label="Close dialog">
      <img src={CloseCross} alt="Close" />
    </CloseButton>
  </CloseButtonContainer>
);

const Modal = (props: ModalProps) => {
  const { children, footer, open, onClose, maxWidth, ariaLabelledBy, ariaDescribedBy } = props;
  const [showGradient, setShowGradient] = useState(false);
  const [scrollableElement, setScrollableElement] = useState<HTMLDivElement | null>(null);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNotAtBottom = scrollTop + clientHeight < scrollHeight;
    setShowGradient(isNotAtBottom);
  };

  useEffect(() => {
    if (!open) {
      setShowGradient(false);
      return;
    }

    if (!scrollableElement || !footer) return;

    const checkOverflow = () => {
      const { scrollHeight, clientHeight } = scrollableElement;
      setShowGradient(scrollHeight > clientHeight);
    };

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(scrollableElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [open, footer, scrollableElement]);

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      <Fade in={open}>
        <StyledBox $maxWidth={maxWidth}>
          <CloseRow onClick={onClose} />
          <ScrollableContent ref={setScrollableElement} onScroll={footer ? handleScroll : undefined}>
            <ModalContent>{children}</ModalContent>
            {footer && showGradient && <GradientFade />}
          </ScrollableContent>
          {footer && <FixedFooter>{footer}</FixedFooter>}
        </StyledBox>
      </Fade>
    </MuiModal>
  );
};

export { Modal };
